import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from '../css/components/navbar.module.css';
import Button from './Button'; 
import { useAuth } from '../context/AuthContext'; 
import { useNavigate, useLocation } from 'react-router-dom';
import { uploadMyProfilePicture, deleteMyProfilePicture } from '../api/userManagement/userManagementApi';
import { useConsultation } from '../context/ConsultationContext';

export function Navbar({ backTo, backReplace = false, onBack, hideBack = false }) {
  const { user, logout, updateUser } = useAuth(); // Get user and logout from AuthContext
  const navigate = useNavigate();
  const location = useLocation();
  const { isInConsultation, endConsultation } = useConsultation();
  const roleName = user?.rolename || user?.roleName || user?.role || '';
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }

    if (isInConsultation) {
      endConsultation();
      navigate('/customer/clinic/consultations');
      return;
    }

    // Force all Profile-related pages to go to Profiles dashboard
    const path = String(location?.pathname || '');
    if (path.includes('/Profiles') || path.includes('/customer/profile')) {
      navigate('/customer/profiles');
      return;
    }
    if (backTo) {
      navigate(backTo, { replace: Boolean(backReplace) });
      return;
    }
    window.history.back();
  };

  const handleAvatarClick = () => {
    if (uploading) return;
    if (!user) return;
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleAvatarKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleAvatarClick();
    }
  };

  const handleDeleteAvatar = async (e) => {
    try {
      if (e?.stopPropagation) e.stopPropagation();
      if (uploading) return;
      if (!user?.profilePicUrl) return;
      setShowDeleteConfirm(true);
    } catch (err) {
      console.error('Profile picture delete failed:', err);
    } finally {
      // no-op: actual delete happens after user confirms
    }
  };

  const handleConfirmDelete = async () => {
    try {
      if (uploading) return;
      if (!user?.profilePicUrl) {
        setShowDeleteConfirm(false);
        return;
      }
      setUploading(true);
      await deleteMyProfilePicture();
      updateUser({ profilePicUrl: null });
      setShowDeleteConfirm(false);
    } catch (err) {
      console.error('Profile picture delete failed:', err);
      setShowDeleteConfirm(false);
    } finally {
      setUploading(false);
    }
  };

  const handleAvatarFileChange = async (e) => {
    try {
      const file = e?.target?.files?.[0];
      if (!file) return;
      if (!String(file.type || '').startsWith('image/')) return;

      setUploading(true);
      const result = await uploadMyProfilePicture(file);
      if (result?.profilePicUrl) {
        updateUser({ profilePicUrl: result.profilePicUrl });
      }
    } catch (err) {
      console.error('Profile picture upload failed:', err);
    } finally {
      setUploading(false);
      if (e?.target) e.target.value = '';
    }
  };

  return (
    <header className={styles.header}>
      {showDeleteConfirm
        ? createPortal(
            <div
              className={styles.confirmBackdrop}
              onClick={() => setShowDeleteConfirm(false)}
              role="presentation"
            >
              <div
                className={styles.confirmModal}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Confirm delete profile picture"
              >
                <div className={styles.confirmTitle}>Delete profile picture?</div>
                <div className={styles.confirmBody}>
                  This will remove your current profile picture.
                </div>
                <div className={styles.confirmActions}>
                  <button
                    type="button"
                    className={styles.confirmBtnSecondary}
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={uploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.confirmBtnDanger}
                    onClick={handleConfirmDelete}
                    disabled={uploading}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      <div className={styles.content}>
        <div className={styles.nav}>
          <div className={styles.logo}>
            <img src="/images/mediintellogo.png" alt="MediIntel Logo" className={styles.logoImage} />
            <span className={styles.logoText}>MediIntel</span>
          </div>
          {!hideBack ? (
            <Button
              variant="primary"
              className={styles.btnGhost}
              onClick={handleBack}
            >
              <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </Button>
          ) : null}
        </div>

        <div className={styles.nav}>
          <div
            className={styles.avatar}
            onClick={handleAvatarClick}
            onKeyDown={handleAvatarKeyDown}
            role={user ? 'button' : undefined}
            tabIndex={user ? 0 : undefined}
            aria-label={user ? 'Upload profile picture' : undefined}
          >
            {user?.profilePicUrl ? (
              <img
                src={user.profilePicUrl}
                alt="Profile"
                className={styles.avatarImg}
                onError={(ev) => {
                  try { ev.currentTarget.style.display = 'none'; } catch (_) {}
                }}
              />
            ) : (
              user?.name ? user.name.charAt(0).toUpperCase() : 'U'
            )}
            {user ? (
              <div className={styles.avatarOverlay} aria-hidden="true">
                <button
                  type="button"
                  className={styles.avatarAction}
                  onClick={(e) => {
                    if (e?.stopPropagation) e.stopPropagation();
                    handleAvatarClick();
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </button>
                {user?.profilePicUrl ? (
                  <button
                    type="button"
                    className={styles.avatarAction}
                    onClick={handleDeleteAvatar}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>
                ) : null}
              </div>
            ) : null}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleAvatarFileChange}
            />
          </div>
          <div className={styles.userInfo}>
            <span className={styles.userName}>
              {user?.name && user?.surname
                ? `${user.name} ${user.surname}`
                : 'User'} {/* Show firstname and surname or 'User' if not logged in */}
            </span>
            {roleName ? <span className={styles.userRole}>{roleName}</span> : null}
          </div>
          <Button
            variant="secondary"
            className={styles.btnGhost}
            onClick={logout} // Use logout from AuthContext
          >
            <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Logout
          </Button>
        </div>
      </div>
    </header>
  );
}

export default Navbar;